// Low-level Telegram channel senders for the manager.
//
// Depends only on Node's https module, the runtime log leaf, and the pure
// delivery-hint gate — no other manager module — so it sits in the channel
// leaf layer importable by the delivery orchestration in manager-worker.ts
// without a cycle. See ADR-0011.

import https from 'node:https';
import { log } from '../runtime.js';
import { hintProbability, pickHintVariant, shouldIncludeHint } from '../../delivery-hint.js';

/**
 * Call Telegram Bot API using Node's https module (not fetch).
 * Node's native fetch (undici) can't reach api.telegram.org on some networks.
 */
export function telegramApiCall(botToken: string, method: string, body: unknown): Promise<{ ok: boolean; description?: string }> {
  return new Promise((resolve, reject) => {
    const postData = JSON.stringify(body);
    const req = https.request({
      hostname: 'api.telegram.org',
      port: 443,
      path: `/bot${botToken}/${method}`,
      method: 'POST',
      family: 4,
      timeout: 10_000,
      headers: { 'Content-Type': 'application/json', 'Content-Length': Buffer.byteLength(postData) },
    }, (res) => {
      let data = '';
      res.on('data', (d) => { data += d; });
      res.on('end', () => {
        try { resolve(JSON.parse(data)); } catch { reject(new Error('Invalid JSON from Telegram API')); }
      });
    });
    req.on('error', reject);
    req.on('timeout', () => { req.destroy(); reject(new Error('Telegram API timeout')); });
    req.write(postData);
    req.end();
  });
}

export async function maybeSendTelegramFollowUpHint(
  agentCodeName: string,
  botToken: string,
  chatId: string | number,
): Promise<void> {
  if (!shouldIncludeHint(hintProbability(agentCodeName))) return;
  const hint = pickHintVariant();
  try {
    const result = await telegramApiCall(botToken, 'sendMessage', { chat_id: chatId, text: hint });
    if (!result.ok) {
      // Bot API resolves `{ ok: false, description }` for app-level failures
      // (chat not found, bot blocked, rate limit). Without this branch the
      // hint drops silently despite the PR promising logged failures.
      log(`[delivery-hint] Telegram follow-up failed for '${agentCodeName}': ${result.description ?? 'unknown'}`);
      return;
    }
    log(`[delivery-hint] Telegram follow-up hint posted for '${agentCodeName}'`);
  } catch (err) {
    // Hint is a best-effort discoverability nudge — logging is enough.
    log(`[delivery-hint] Telegram follow-up failed for '${agentCodeName}': ${(err as Error).message}`);
  }
}
